import NextImage from 'next/image'
import cn from 'classnames'
import { Tile, TileCaption, TileImage, TileOverlay } from '@mc/design-system'
import { cloudflareLoader } from '@mc/image-cloudflare'
import { BREAKPOINTS } from '@mc/media'
import classes from './HighContrastHero.module.scss'
import { GetNotifiedModal } from './components/GetNotifiedModal'
import { GetNotifiedModalContextProvider } from './components/GetNotifiedModalContext'
import { HeroContent } from './components/HeroContent'
import { SampleProps } from './components/SampleAction'
import { ShareProps } from './components/ShareAction'
import { TrailerProps } from './components/TrailerAction'
import { TrackProps } from './events'

interface DesktopProps {
  image: string | null | undefined
  header: string | null | undefined
  subheader: string | null | undefined
  nameplate: JSX.Element | string | null | undefined
  nameplateAlt?: string | null | undefined
  alternativeNameplate?: boolean
  trackProps: TrackProps
  trailerProps?: TrailerProps
  sampleProps?: SampleProps
  shareProps?: ShareProps
  releaseDate?: string
}

const Desktop = ({
  image,
  header,
  subheader,
  nameplate,
  nameplateAlt,
  alternativeNameplate,
  trackProps,
  trailerProps,
  sampleProps,
  shareProps,
  releaseDate,
}: DesktopProps) => (
  <>
    <div className='col-6 mc-p-0' style={{ height: 700 }}>
      {image && (
        <Tile aspectRatio='auto'>
          <TileImage
            image={
              <NextImage
                alt=''
                loader={cloudflareLoader}
                // Desktop image is visible at MD breakpoint and above
                sizes={`(max-width: ${BREAKPOINTS.MD - 1}px) 0vw, 100vw`}
                src={image}
                layout='fill'
                objectFit='cover'
                priority
              />
            }
          />
          <TileOverlay color='0,0,0' />
        </Tile>
      )}
    </div>
    <div className={cn('col-6 mc-p-0 d-flex', classes.heroContent)}>
      <Tile aspectRatio='auto'>
        <TileCaption position='center center'>
          <HeroContent
            namePlateCls={alternativeNameplate ? 'col-9' : 'col-8'}
            descriptionCls='col-md-11 col-lg-10 mc-text-small'
            ctaCls='col-md-10 col-lg-6'
            actionsCls='col-auto'
            header={header}
            subheader={subheader}
            nameplate={nameplate}
            nameplateAlt={nameplateAlt}
            alternativeNameplate={alternativeNameplate}
            trackProps={trackProps}
            trailerProps={trailerProps}
            sampleProps={sampleProps}
            shareProps={shareProps}
            releaseDate={releaseDate}
          />
        </TileCaption>
      </Tile>
    </div>
  </>
)

interface MobileProps {
  image: string | null | undefined
  header: string | null | undefined
  subheader: string | null | undefined
  nameplate: JSX.Element | string | null | undefined
  nameplateAlt?: string | null | undefined
  alternativeNameplate?: boolean
  trackProps: TrackProps
  trailerProps?: TrailerProps
  sampleProps?: SampleProps
  shareProps?: ShareProps
  releaseDate?: string
}

const Mobile = ({
  image,
  header,
  subheader,
  nameplate,
  nameplateAlt,
  alternativeNameplate,
  trackProps,
  trailerProps,
  sampleProps,
  shareProps,
  releaseDate,
}: MobileProps) => (
  <>
    <div className={classes.imageContainer}>
      {image && (
        <Tile aspectRatio='auto'>
          <TileImage
            image={
              <NextImage
                alt=''
                loader={cloudflareLoader}
                src={image}
                layout='fill'
                // Mobile image is visible at MD breakpoint and below
                sizes={`(max-width: ${BREAKPOINTS.MD + 1}px) 100vw, 0vw`}
                priority
              />
            }
            className={classes.heroImage}
          />
          <TileOverlay color='0,0,0' />
        </Tile>
      )}
    </div>

    <div className={classes.contentContainer}>
      <div className='d-block d-sm-none'>
        <HeroContent
          showSeparator={false}
          namePlateCls={alternativeNameplate ? 'col-11' : 'col-8'}
          namePlateSize={150}
          descriptionCls='mc-my-3 mc-text-small'
          ctaCls='col-12 col-sm-9'
          actionsCls='col-auto'
          header={header}
          subheader={subheader}
          nameplate={nameplate}
          nameplateAlt={nameplateAlt}
          alternativeNameplate={alternativeNameplate}
          trackProps={trackProps}
          trailerProps={trailerProps}
          sampleProps={sampleProps}
          shareProps={shareProps}
          releaseDate={releaseDate}
        />
      </div>
      <div className='d-none d-sm-block'>
        <HeroContent
          showSeparator={false}
          namePlateCls={alternativeNameplate ? 'col-9' : 'col-8'}
          descriptionCls='mc-my-3 mc-text-small'
          ctaCls='col-12 col-sm-9'
          actionsCls='col-auto'
          header={header}
          subheader={subheader}
          nameplate={nameplate}
          nameplateAlt={nameplateAlt}
          alternativeNameplate={alternativeNameplate}
          trackProps={trackProps}
          trailerProps={trailerProps}
          sampleProps={sampleProps}
          shareProps={shareProps}
          releaseDate={releaseDate}
        />
      </div>
    </div>
  </>
)

interface HighContrastHeroProps {
  header: string | null | undefined
  subheader: string | null | undefined
  desktopImage: string | null | undefined
  mobileImage: string | null | undefined
  nameplate: JSX.Element | string | null | undefined
  nameplateAlt?: string | null | undefined
  alternativeNameplate?: boolean
  trackProps: TrackProps
  trailerProps?: TrailerProps
  sampleProps?: SampleProps
  shareProps?: ShareProps
  releaseDate?: string
  modalProps?: {
    mobileImage: string | null | undefined
    desktopImage: string | null | undefined
    slug: string | null | undefined
    episodeSlug: string | null | undefined
  }
}

export const HighContrastHero = ({
  nameplate,
  header,
  subheader,
  desktopImage,
  mobileImage,
  nameplateAlt,
  alternativeNameplate,
  trackProps,
  trailerProps,
  sampleProps,
  shareProps,
  releaseDate,
  modalProps,
}: HighContrastHeroProps) => (
  <GetNotifiedModalContextProvider
    mobileImage={modalProps?.mobileImage}
    desktopImage={modalProps?.desktopImage}
    slug={modalProps?.slug}
    episodeSlug={modalProps?.episodeSlug}
  >
    <section data-testid='hero-section' className='d-none d-md-flex'>
      <Desktop
        nameplate={nameplate}
        header={header}
        subheader={subheader}
        image={desktopImage}
        nameplateAlt={nameplateAlt}
        alternativeNameplate={alternativeNameplate}
        trackProps={trackProps}
        trailerProps={trailerProps}
        sampleProps={sampleProps}
        shareProps={shareProps}
        releaseDate={releaseDate}
      />
    </section>

    <section
      data-testid='hero-section'
      className='d-flex flex-column d-md-none'
    >
      <Mobile
        nameplate={nameplate}
        header={header}
        subheader={subheader}
        image={mobileImage}
        nameplateAlt={nameplateAlt}
        alternativeNameplate={alternativeNameplate}
        trackProps={trackProps}
        trailerProps={trailerProps}
        sampleProps={sampleProps}
        shareProps={shareProps}
        releaseDate={releaseDate}
      />
    </section>

    <GetNotifiedModal />
  </GetNotifiedModalContextProvider>
)
